import React from 'react';
import { motion } from 'framer-motion';
import { 
  FiBriefcase, FiTruck, FiPackage, FiCheckCircle, 
  FiZap, FiStar, FiGlobe, FiAward 
} from 'react-icons/fi';
import { useScrollAnim, V } from '../hooks/useScrollAnimation';

const STATS = [
  { num: "Multi", label: "Industry Solutions", sub: "Business Focused" },
  { num: "PAN", label: "India Reach", sub: "Nationwide Network" },
  { num: "B2B", label: "Business Support", sub: "Procurement Ready" },
  { num: "24/7", label: "Client Assistance", sub: "Dedicated Support" },
];

const WHY = [
  {
    Icon: FiBriefcase,
    title: "Business Focused Solutions",
    desc: "Tailored packaging and supply programs designed around business requirements."
  },
  {
    Icon: FiTruck,
    title: "Nationwide Distribution",
    desc: "Reliable supply and distribution support across multiple regions and industries."
  },
  {
    Icon: FiPackage,
    title: "Procurement Support",
    desc: "Helping businesses source products efficiently through structured procurement solutions."
  },
  {
    Icon: FiCheckCircle,
    title: "Quality Commitment",
    desc: "Focused on delivering reliable products and consistent service standards."
  },
  {
    Icon: FiZap,
    title: "Operational Efficiency",
    desc: "Streamlined processes that help businesses reduce delays and improve performance."
  },
  {
    Icon: FiStar,
    title: "Client Partnerships",
    desc: "Building long-term relationships through trust, transparency, and support."
  },
  {
    Icon: FiGlobe,
    title: "Industry Expertise",
    desc: "Supporting food service, retail, distribution, hospitality, and commercial sectors."
  },
  {
    Icon: FiAward,
    title: "Growth-Oriented Approach",
    desc: "Continuously expanding capabilities to meet evolving business needs."
  }
];

const HIGHLIGHTS = [
  {
    title: "Business Solutions",
    desc: "Tailored support for modern organizations."
  },
  {
    title: "Industry Expertise",
    desc: "Serving multiple business sectors."
  },
  {
    title: "Reliable Partnerships",
    desc: "Focused on long-term business relationships."
  }
];

export default function About() {
  const header = useScrollAnim(0.1);
  const highlights = useScrollAnim(0.12);
  const stats = useScrollAnim(0.15);
  const grid = useScrollAnim(0.08);
  const trust = useScrollAnim(0.2);

  return (
    <section id="about" style={{
      padding: 'var(--spy) 0',
      background: 'linear-gradient(150deg,#fff 0%,#FFF5F9 40%,#FFFBF5 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <div className="dot-grid" style={{ position: 'absolute', inset: 0, opacity: 0.5, pointerEvents: 'none' }} />

      <div className="container">
        {/* Header */}
        <motion.div
          ref={header.ref}
          initial="hidden"
          animate={header.controls}
          variants={V.stagger}
          style={{ textAlign: 'center', marginBottom: '4rem' }}
        >
          <motion.span
            variants={V.child}
            style={{
              display: 'block',
              fontFamily: 'var(--font-body)',
              fontSize: '0.78rem',
              fontWeight: 700,
              background: 'linear-gradient(135deg,#E91E8C,#FF6B2B)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textTransform: 'uppercase',
              letterSpacing: '0.15em',
              marginBottom: '0.8rem'
            }}
          >
            WHY SPE
          </motion.span>

          <motion.h2
            variants={V.child}
            style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 700,
              fontSize: 'clamp(2.3rem, 5.2vw, 3.5rem)',
              color: 'var(--text)',
              lineHeight: 1.1,
              marginBottom: '1.1rem'
            }}
          >
            A Trusted Partner For{' '}
            <span style={{
              background: 'linear-gradient(135deg,#E91E8C,#FF6B2B)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              Business Growth
            </span>
          </motion.h2>

          <motion.p
            variants={V.child}
            style={{
              fontFamily: 'var(--font-body)',
              color: 'var(--text-muted)',
              fontSize: '1.05rem',
              maxWidth: 680,
              margin: '0 auto',
              lineHeight: 1.7
            }}
          >
            SPE helps businesses streamline procurement, packaging, and supply operations through reliable solutions, 
            nationwide reach, and a commitment to operational excellence.
          </motion.p>
        </motion.div>

        {/* 3 Highlight Cards */}
        <motion.div
          ref={highlights.ref}
          initial="hidden"
          animate={highlights.controls}
          variants={V.stagger2}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '1.8rem',
            marginBottom: '5rem'
          }}
        >
          {HIGHLIGHTS.map((item, i) => (
            <motion.div
              key={i}
              variants={V.child}
              whileHover={{ y: -8 }}
              style={{
                background: '#fff',
                padding: '2rem 1.8rem',
                borderRadius: 24,
                border: '1.5px solid rgba(233,30,140,0.1)',
                boxShadow: 'var(--shadow-card)',
                textAlign: 'center'
              }}
            >
              <h3 style={{
                fontFamily: 'var(--font-display)',
                fontSize: '1.35rem',
                fontWeight: 700,
                marginBottom: '0.8rem',
                color: 'var(--text)'
              }}>
                {item.title}
              </h3>
              <p style={{
                fontFamily: 'var(--font-body)',
                color: 'var(--text-muted)',
                lineHeight: 1.65
              }}>
                {item.desc}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* Stats */}
        <motion.div
          ref={stats.ref}
          initial="hidden"
          animate={stats.controls}
          variants={V.stagger2}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: '1.5rem',
            marginBottom: '5rem'
          }}
        >
          {STATS.map((s, i) => (
            <motion.div
              key={i}
              variants={V.child}
              whileHover={{ scale: 1.05, y: -6 }}
              style={{
                textAlign: 'center',
                padding: '2rem 1.2rem',
                background: '#fff',
                border: '1.5px solid rgba(233,30,140,0.1)',
                borderRadius: 22,
                boxShadow: 'var(--shadow-card)'
              }}
            >
              <p style={{
                fontFamily: 'var(--font-accent)',
                fontSize: 'clamp(2.2rem, 4vw, 3rem)',
                fontWeight: 700,
                background: 'linear-gradient(135deg,#E91E8C,#FF6B2B)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                marginBottom: '0.4rem'
              }}>
                {s.num}
              </p>
              <p style={{
                fontFamily: 'var(--font-body)',
                fontWeight: 700,
                color: 'var(--text)',
                fontSize: '1.05rem',
                marginBottom: '0.3rem'
              }}>
                {s.label}
              </p>
              <p style={{ fontFamily: 'var(--font-body)', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                {s.sub}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* Why SPE Grid */}
        <motion.div
          ref={grid.ref}
          initial="hidden"
          animate={grid.controls}
          variants={V.stagger}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: '1.8rem',
            marginBottom: '5rem'
          }}
        >
          {WHY.map(({ Icon, title, desc }, index) => (
            <motion.div
              key={index}
              variants={V.child}
              whileHover={{ y: -10, boxShadow: '0 20px 50px rgba(233,30,140,0.15)' }}
              style={{
                padding: '2rem',
                background: '#fff',
                border: '1.5px solid rgba(233,30,140,0.1)',
                borderRadius: 24,
                boxShadow: 'var(--shadow-card)',
                transition: 'all 0.4s ease'
              }}
            >
              <div style={{
                width: 56,
                height: 56,
                borderRadius: 16,
                background: 'linear-gradient(135deg,rgba(233,30,140,0.1),rgba(255,107,43,0.08))',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '1.4rem',
                color: '#E91E8C'
              }}>
                <Icon size={28} />
              </div>

              <h3 style={{
                fontFamily: 'var(--font-display)',
                fontWeight: 700,
                fontSize: '1.28rem',
                marginBottom: '0.9rem',
                color: 'var(--text)'
              }}>
                {title}
              </h3>

              <p style={{
                fontFamily: 'var(--font-body)',
                color: 'var(--text-muted)',
                lineHeight: 1.7,
                fontSize: '0.93rem'
              }}>
                {desc}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* Trust Banner */}
        <motion.div
          ref={trust.ref}
          initial="hidden"
          animate={trust.controls}
          variants={V.scaleIn}
          style={{
            padding: 'clamp(2.5rem, 6vw, 4rem)',
            borderRadius: 28,
            background: 'linear-gradient(135deg,#E91E8C,#FF6B2B,#FFB800)',
            boxShadow: '0 25px 70px rgba(233,30,140,0.4)',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <div style={{ position: 'absolute', top: -60, right: -60, width: 260, height: 260, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
          <div style={{ position: 'absolute', bottom: -50, left: -50, width: 220, height: 220, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />

          <div style={{ position: 'relative', textAlign: 'center', maxWidth: '860px', margin: '0 auto' }}>
            <p style={{
              fontFamily: 'var(--font-body)',
              fontSize: '0.8rem',
              fontWeight: 700,
              color: 'rgba(255,255,255,0.75)',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              marginBottom: '1rem'
            }}>
              Partnering With Businesses Across Industries
            </p>

            <h3 style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 700,
              fontSize: 'clamp(1.8rem, 4.5vw, 2.6rem)',
              color: '#fff',
              lineHeight: 1.2,
              marginBottom: '1.2rem'
            }}>
              Building Long-Term Business Partnerships
            </h3>

            <p style={{
              fontFamily: 'var(--font-body)',
              color: 'rgba(255,255,255,0.85)',
              fontSize: '1.05rem',
              lineHeight: 1.75,
              maxWidth: 620,
              margin: '0 auto 2.5rem'
            }}>
              From packaging and procurement to distribution and business support, SPE is committed to helping 
              organizations operate efficiently and grow with confidence.
            </p>

            <motion.a
              href="https://wa.me/919821991932?text=Hi SPE, I would like to schedule a consultation."
              target="_blank"
              rel="noreferrer"
              whileHover={{ scale: 1.06 }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 10,
                padding: '1.1rem 2.4rem',
                background: '#fff',
                color: '#E91E8C',
                fontWeight: 700,
                fontSize: '1.02rem',
                borderRadius: 99,
                textDecoration: 'none',
                boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
              }}
            >
              Schedule Consultation →
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}